/**
 * MODULE: domain/pages/frontend/components/PagesShortcutsModal.tsx
 * PURPOSE: Pages keyboard shortcuts reference dialog and global key bindings
 */

import { Modal, ModalBody, ModalHeader } from 'flowbite-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import {
  formatModShortcut,
  isModKey,
  isPagesShortcutsTypingTarget,
} from '../utils/pageKeyboardShortcutsUtils';

export type PagesShortcutHandlers = {
  focusSearch: () => void;
  createNote: () => void;
  toggleSidebar?: () => void;
  openShare?: () => void;
  openHistory?: () => void;
  focusTitle?: () => void;
  focusEditor?: () => void;
  focusComments?: () => void;
  exportMarkdown?: () => void;
  copyMarkdown?: () => void;
  copyPageLink?: () => void;
  exportDatabaseCsv?: () => void;
  printPage?: () => void;
  togglePageLock?: () => void;
};

const noop = () => undefined;

const DEFAULT_HANDLERS: PagesShortcutHandlers = {
  focusSearch: noop,
  createNote: noop,
};

function isPagesRouteActive(): boolean {
  return typeof document !== 'undefined' && Boolean(document.querySelector('[data-testid="pages-root"]'));
}

type PagesShortcutsContextValue = {
  openShortcutsModal: () => void;
  registerHandlers: (handlers: Partial<PagesShortcutHandlers>) => void;
};

const PagesShortcutsContext = createContext<PagesShortcutsContextValue | null>(null);

export function usePagesShortcuts(): PagesShortcutsContextValue {
  const ctx = useContext(PagesShortcutsContext);
  if (!ctx) {
    throw new Error('usePagesShortcuts must be used within PagesShortcutsProvider');
  }
  return ctx;
}

type PagesShortcutsModalProps = {
  open: boolean;
  onClose: () => void;
};

export function PagesShortcutsModal({ open, onClose }: PagesShortcutsModalProps) {
  const rows: Array<{ context: string; shortcut: string; action: string }> = [
    { context: 'Navigation', shortcut: formatModShortcut('\\'), action: 'Toggle sidebar (focus mode)' },
    { context: 'Navigation', shortcut: formatModShortcut('K'), action: 'Focus sidebar search' },
    { context: 'Navigation', shortcut: formatModShortcut('Shift+N'), action: 'Create a new note page' },
    { context: 'Page', shortcut: formatModShortcut('Shift+T'), action: 'Focus page title' },
    { context: 'Page', shortcut: formatModShortcut('Shift+E'), action: 'Focus note editor' },
    { context: 'Page', shortcut: formatModShortcut('Shift+H'), action: 'Open version history (note pages)' },
    { context: 'Page', shortcut: formatModShortcut('Shift+K'), action: 'Lock or unlock page (when permitted)' },
    { context: 'Page', shortcut: formatModShortcut('Shift+P'), action: 'Open share dialog (when permitted)' },
    { context: 'Page', shortcut: formatModShortcut('Shift+L'), action: 'Copy link to this page' },
    { context: 'Page', shortcut: formatModShortcut('Shift+M'), action: 'Download note page as Markdown (.md)' },
    { context: 'Page', shortcut: formatModShortcut('Shift+U'), action: 'Copy note page as Markdown to clipboard' },
    {
      context: 'Database page',
      shortcut: formatModShortcut('Shift+D'),
      action: 'Download database rows as CSV',
    },
    { context: 'Page', shortcut: formatModShortcut('Shift+O'), action: 'Print page or save as PDF' },
    { context: 'Page', shortcut: formatModShortcut('Shift+C'), action: 'Jump to comments and focus compose' },
    { context: 'Comments', shortcut: formatModShortcut('Enter'), action: 'Post comment (when compose field is focused)' },
    { context: 'Editor', shortcut: '/', action: 'Open slash command menu while typing' },
    { context: 'Editor', shortcut: formatModShortcut('B'), action: 'Bold selected text' },
    { context: 'Editor', shortcut: formatModShortcut('I'), action: 'Italic selected text' },
    { context: 'Help', shortcut: '?', action: 'Open this shortcuts panel (not when typing in a field)' },
    { context: 'Universal', shortcut: 'Esc', action: 'Close the topmost open overlay or menu' },
  ];

  return (
    <Modal show={open} onClose={onClose} size="lg" data-testid="pages-shortcuts-modal">
      <ModalHeader>Keyboard shortcuts</ModalHeader>
      <ModalBody>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          While the Pages module is open, {formatModShortcut('K')} focuses sidebar search instead of the
          global assistant. Other shortcuts apply only in the listed contexts.
        </p>
        <div className="max-h-[min(60vh,28rem)] overflow-y-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-600">
                <th className="py-2 pr-2 font-medium text-gray-700 dark:text-gray-200">Context</th>
                <th className="py-2 pr-2 font-medium text-gray-700 dark:text-gray-200">Shortcut</th>
                <th className="py-2 font-medium text-gray-700 dark:text-gray-200">Action</th>
              </tr>
            </thead>
            <tbody className="text-gray-800 dark:text-gray-100">
              {rows.map((row) => (
                <tr key={`${row.context}-${row.shortcut}`} className="border-b border-gray-100 dark:border-gray-700/80">
                  <td className="py-2 pr-2 align-top text-gray-500 dark:text-gray-400">{row.context}</td>
                  <td className="py-2 pr-2 align-top font-mono text-xs sm:text-sm">{row.shortcut}</td>
                  <td className="py-2 align-top">{row.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ModalBody>
    </Modal>
  );
}

function PagesKeyboardShortcutsEffect({
  handlersRef,
  openShortcutsModal,
}: {
  handlersRef: MutableRefObject<PagesShortcutHandlers>;
  openShortcutsModal: () => void;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isPagesRouteActive()) return;

      if (isModKey(event)) {
        const key = event.key.toLowerCase();
        if (key === 'k' && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          event.stopImmediatePropagation();
          handlersRef.current.focusSearch();
          return;
        }
        if (key === '\\' && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          event.stopImmediatePropagation();
          handlersRef.current.toggleSidebar?.();
          return;
        }
        if (event.shiftKey && key === 'n') {
          event.preventDefault();
          event.stopImmediatePropagation();
          handlersRef.current.createNote();
          return;
        }
        if (event.shiftKey && key === 't') {
          event.preventDefault();
          event.stopImmediatePropagation();
          handlersRef.current.focusTitle?.();
          return;
        }
        if (event.shiftKey && key === 'e') {
          event.preventDefault();
          event.stopImmediatePropagation();
          handlersRef.current.focusEditor?.();
          return;
        }
        if (event.shiftKey && key === 'h') {
          event.preventDefault();
          event.stopImmediatePropagation();
          handlersRef.current.openHistory?.();
          return;
        }
        if (event.shiftKey && key === 'k') {
          event.preventDefault();
          event.stopImmediatePropagation();
          handlersRef.current.togglePageLock?.();
          return;
        }
        if (event.shiftKey && key === 'p') {
          event.preventDefault();
          event.stopImmediatePropagation();
          handlersRef.current.openShare?.();
          return;
        }
        if (event.shiftKey && key === 'l') {
          event.preventDefault();
          event.stopImmediatePropagation();
          handlersRef.current.copyPageLink?.();
          return;
        }
        if (event.shiftKey && key === 'm') {
          event.preventDefault();
          event.stopImmediatePropagation();
          handlersRef.current.exportMarkdown?.();
          return;
        }
        if (event.shiftKey && key === 'u') {
          event.preventDefault();
          event.stopImmediatePropagation();
          handlersRef.current.copyMarkdown?.();
          return;
        }
        if (event.shiftKey && key === 'd') {
          event.preventDefault();
          event.stopImmediatePropagation();
          handlersRef.current.exportDatabaseCsv?.();
          return;
        }
        if (event.shiftKey && key === 'o') {
          event.preventDefault();
          event.stopImmediatePropagation();
          handlersRef.current.printPage?.();
          return;
        }
        if (event.shiftKey && key === 'c') {
          event.preventDefault();
          event.stopImmediatePropagation();
          handlersRef.current.focusComments?.();
          return;
        }
        return;
      }

      if (event.key !== '?') return;
      if (isPagesShortcutsTypingTarget(document.activeElement)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      openShortcutsModal();
    };

    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [handlersRef, openShortcutsModal]);

  return null;
}

export function PagesShortcutsProvider({
  children,
  onToggleSidebar,
}: {
  children: ReactNode;
  onToggleSidebar?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const handlersRef = useRef<PagesShortcutHandlers>({ ...DEFAULT_HANDLERS });

  const openShortcutsModal = useCallback(() => setOpen(true), []);
  const closeShortcutsModal = useCallback(() => setOpen(false), []);

  const registerHandlers = useCallback((handlers: Partial<PagesShortcutHandlers>) => {
    handlersRef.current = { ...handlersRef.current, ...handlers };
  }, []);

  useEffect(() => {
    if (onToggleSidebar) {
      handlersRef.current.toggleSidebar = onToggleSidebar;
    }
  }, [onToggleSidebar]);

  const value = useMemo(
    () => ({ openShortcutsModal, registerHandlers }),
    [openShortcutsModal, registerHandlers]
  );

  return (
    <PagesShortcutsContext.Provider value={value}>
      {children}
      <PagesShortcutsModal open={open} onClose={closeShortcutsModal} />
      <PagesKeyboardShortcutsEffect handlersRef={handlersRef} openShortcutsModal={openShortcutsModal} />
    </PagesShortcutsContext.Provider>
  );
}
